import type {BoardCardAddress} from '@github-ui/board-core'
import {replaceEqualDeep} from '@github-ui/react-query'

import type {ApplyItemEdit, BoardItemEdit} from './board-document-adapter'
import {type BoardPlacement, readBoardPlacement} from './board-placement'
import type {
  SavedViewsBoardAdapter,
  SavedViewsBoardItem,
  SavedViewsBoardPayload,
  SavedViewsBoardResolvedItem,
} from './types'

type ExpectedValue = {path: string[]; value: unknown}
type Confirmation<TItem> = {
  item: TItem
  values: Map<string, ExpectedValue>
  axes: Map<'columnKey' | 'rowKey', readonly string[]>
}

/**
 * Versions describe local interactions, not server revisions. A read spanning even a completed
 * interaction must be discarded; starting another request is the only way to verify its result.
 */
export class BoardLiveUpdateProtection<TItem extends SavedViewsBoardItem> {
  #versions = new Map<string, number>()
  #interactions = new Map<'drag' | 'dialog', Set<string>>()
  #pending = new Map<number, string>()
  #confirmations = new Map<string, Confirmation<TItem>>()
  #projectItem: (item: TItem) => unknown

  constructor(projectItem: (item: TItem) => unknown = item => item) {
    this.#projectItem = projectItem
  }

  version(id: string): number {
    return this.#versions.get(id) ?? 0
  }

  touch(id: string) {
    this.#versions.set(id, this.version(id) + 1)
  }

  isBlocked(id: string): boolean {
    return (
      [...this.#interactions.values()].some(ids => ids.has(id)) ||
      [...this.#pending.values()].some(pendingId => pendingId === id)
    )
  }

  setInteraction(kind: 'drag' | 'dialog', cards: readonly BoardCardAddress[]): string[] {
    const previous = this.#interactions.get(kind) ?? new Set<string>()
    const next = new Set(cards.map(card => card.itemId))
    this.#interactions.set(kind, next)
    const changed = [...new Set([...previous, ...next])].filter(id => previous.has(id) !== next.has(id))
    for (const id of changed) this.touch(id)
    return changed
  }

  setPending(next: Map<number, string>): string[] {
    const changed = new Set<string>()
    for (const [key, id] of this.#pending) if (next.get(key) !== id) changed.add(id)
    for (const [key, id] of next) if (this.#pending.get(key) !== id) changed.add(id)
    this.#pending = next
    for (const id of changed) this.touch(id)
    return [...changed]
  }

  confirm<TMetadata>(
    edit: BoardItemEdit,
    payload: SavedViewsBoardPayload<TItem, TMetadata>,
    applyItemEdit?: ApplyItemEdit<TItem>,
    updateMovedItem?: SavedViewsBoardAdapter<TItem, TMetadata>['updateMovedItem'],
  ): boolean {
    const item = payload.boardData.data.cells.flatMap(cell => cell.items).find(card => card.globalId === edit.id)
    if (!item) return false

    const confirmation = this.#confirmations.get(edit.id) ?? {item, values: new Map(), axes: new Map()}
    const placement = readBoardPlacement(edit)
    if (placement) {
      this.#recordPlacement(confirmation, placement, payload, updateMovedItem)
    } else if (applyItemEdit) {
      // Settlement handlers run together, so the cache may not contain the preceding fold yet.
      const changes = this.#recordItem(confirmation, applyItemEdit(confirmation.item, edit))
      for (const [axis, keys] of confirmation.axes) {
        if (!updateMovedItem) continue
        const placed = updateMovedItem(
          confirmation.item,
          {axis: axis === 'columnKey' ? 'columns' : 'rows', keys},
          payload.metadata,
        )
        const placementChanges = changedValues(this.#projectItem(confirmation.item), this.#projectItem(placed))
        // Only release an axis when restoring its placement would undo part of this newer edit.
        if (changes.some(change => placementChanges.some(expected => pathsOverlap(change.path, expected.path)))) {
          confirmation.axes.delete(axis)
        }
      }
    }
    if (confirmation.values.size === 0 && confirmation.axes.size === 0) return false
    this.#confirmations.set(edit.id, confirmation)
    this.touch(edit.id)
    return true
  }

  // Records the axis keys a refreshed item must occupy before its confirmation can be cleared.
  #recordPlacement<TMetadata>(
    confirmation: Confirmation<TItem>,
    placement: BoardPlacement,
    {boardData: {columnField, rowField}, metadata}: SavedViewsBoardPayload<TItem, TMetadata>,
    updateMovedItem: SavedViewsBoardAdapter<TItem, TMetadata>['updateMovedItem'],
  ) {
    // The returned axis keys verify a moved field even when its display updater leaves it unchanged.
    for (const [key, {path}] of confirmation.values) {
      const field = path[0]
      if (field && (field === columnField || field === rowField) && placement.axes[field]) {
        confirmation.values.delete(key)
      }
    }
    let item = confirmation.item
    if (columnField && placement.axes[columnField]) {
      const {keys} = placement.axes[columnField]
      confirmation.axes.set('columnKey', keys)
      if (updateMovedItem) item = updateMovedItem(item, {axis: 'columns', keys}, metadata)
    }
    if (rowField && placement.axes[rowField]) {
      const {keys} = placement.axes[rowField]
      confirmation.axes.set('rowKey', keys)
      if (updateMovedItem) item = updateMovedItem(item, {axis: 'rows', keys}, metadata)
    }
    this.#recordItem(confirmation, item)
  }

  // Accumulates edited values that a refreshed item must match before its confirmation can be cleared.
  #recordItem(confirmation: Confirmation<TItem>, updated: TItem) {
    const projected = this.#projectItem(updated)
    const changes = changedValues(this.#projectItem(confirmation.item), projected)
    // Parent/child path changes must also refresh earlier expectations so they do not contradict this edit.
    for (const [key, expected] of confirmation.values) {
      if (changes.some(change => pathsOverlap(change.path, expected.path))) {
        confirmation.values.set(key, {...expected, value: valueAt(projected, expected.path)})
      }
    }
    for (const expected of changes) {
      confirmation.values.set(JSON.stringify(expected.path), expected)
    }
    confirmation.item = updated
    return changes
  }

  // Accepts a refresh only if no local interaction invalidated it and it matches all confirmed edits.
  // Matching results clear the confirmation so subsequent refreshes can update or remove the item.
  accept(id: string, version: number, resolved: SavedViewsBoardResolvedItem<TItem> | undefined): boolean {
    if (this.isBlocked(id) || this.version(id) !== version) return false
    const confirmation = this.#confirmations.get(id)
    if (!confirmation) return true
    // An absent query result cannot prove that the index incorporated a confirmed edit.
    if (!resolved) return false
    const projected = this.#projectItem(resolved.item)
    for (const {path, value} of confirmation.values.values()) {
      if (!equal(value, valueAt(projected, path))) return false
    }
    for (const [axis, keys] of confirmation.axes) {
      const actual = new Set(resolved.cells.map(cell => cell[axis]))
      if (actual.size !== keys.length || keys.some(key => !actual.has(key))) return false
    }
    this.#confirmations.delete(id)
    return true
  }
}

function valueAt(value: unknown, path: readonly string[]): unknown {
  for (const key of path) {
    if (!isRecord(value)) return undefined
    value = value[key]
  }
  return value
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype
}

function equal(before: unknown, after: unknown): boolean {
  return replaceEqualDeep(before, after) === before
}

function pathsOverlap(first: readonly string[], second: readonly string[]): boolean {
  return first.slice(0, Math.min(first.length, second.length)).every((key, index) => key === second[index])
}

function changedValues(before: unknown, after: unknown, path: string[] = []): ExpectedValue[] {
  if (equal(before, after)) return []
  if (isRecord(before) && isRecord(after)) {
    return [...new Set([...Object.keys(before), ...Object.keys(after)])].flatMap(key =>
      changedValues(before[key], after[key], [...path, key]),
    )
  }
  return [{path, value: after}]
}
